import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  ListSubheader,
  Alert
} from '@mui/material';

interface AnimalCategory {
  category: string;
  types: { name: string }[];
}

interface NewGroup {
  type: string;
  breed: string;
  category?: string;
}

interface AnimalGroupDialogProps {
  open: boolean;
  onClose: () => void;
  newGroup: NewGroup;
  handleGroupInputChange: (field: keyof NewGroup, value: string) => void;
  animalCategories: AnimalCategory[];
  availableBreeds: string[];
  customBreedName: string;
  handleGroupCustomBreedInput: (value: string) => void;
  handleSaveGroup: () => void;
}

const AnimalGroupDialog: React.FC<AnimalGroupDialogProps> = ({
  open,
  onClose,
  newGroup,
  handleGroupInputChange,
  animalCategories,
  availableBreeds,
  customBreedName,
  handleGroupCustomBreedInput,
  handleSaveGroup,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Add New Animal Type</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Animal Type"
              value={newGroup.type}
              onChange={(e) => handleGroupInputChange('type', e.target.value)}
              required
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                },
              }}
              helperText="Select from predefined types or use 'Other' to add a custom type"
            >
              {animalCategories.map((category) => [
                <ListSubheader key={category.category}>
                  {category.category}
                </ListSubheader>,
                ...category.types.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))
              ])}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Breed"
              value={newGroup.breed}
              onChange={(e) => handleGroupInputChange('breed', e.target.value)}
              required
              disabled={availableBreeds.length === 0}
              helperText="Select from predefined breeds for this animal type"
            >
              {availableBreeds.map((breed) => (
                <MenuItem key={breed} value={breed}>
                  {breed}
                </MenuItem>
              ))}
              <MenuItem value="Custom Breed">
                <em>Custom Breed (specify below)</em>
              </MenuItem>
            </TextField>
          </Grid>
          {newGroup.breed === 'Custom Breed' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Custom Breed Name"
                value={customBreedName}
                onChange={(e) => handleGroupCustomBreedInput(e.target.value)}
                placeholder="Enter a custom breed name"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSaveGroup} 
          variant="contained" 
          color="primary"
          disabled={!newGroup.type || !newGroup.breed || newGroup.breed === 'Custom Breed'}
        >
          Create Animal Group
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnimalGroupDialog;
